/* eslint-disable indent */
import React, {
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './FundingModule.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import {
    AcquisitionFlow,
    getAcquisitionFlow,
    getIpAddress,
} from '../../utils/acquisition-utils';
import { cleanInternalLinkUrl, isAuthorMode, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import SessionStorage from '../../utils/SessionStorage';
import { InputDropdown, InputCheckbox, InputRadio } from 'component-library/index';
import BankDetailInput from './subcomponents/BankDetailInput';
import InlinePushup from '../../utils/utility-components/InlinePushup';
import { Button, Row, Col, Section, Container } from '../../importCL';
import { callAcquisitionBff } from '../../utils/acquisition-api/AcquisitionBff';
import { UUIDv4 } from '../../utils/cob-utils'
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { AcquisitionsLoader } from '../AcquisitionsLoader/AcquisitionsLoader';
import DebitCardFunding from '../DebitCardFunding';
import FundingModuleSelectableCards from './variants/FundingModuleSelectableCards';
import { ISecuredCardFundingAttempt } from '../../types/responses/SecuredCardFundingAttemptResponse';
import { IFlowApplicationData } from '../../types/shared/FlowApplicationData';
import { isNewErrorMessagingFlagActive } from '../../utils/error-message-flag';
import { AxiosError } from 'axios';

export type TFundingModuleVariants = 'defaultEnabled' | 'debitCardEnabled' | 'bankAccountEnabled';
export interface IFundingProps extends MappedComponentProperties {
    testingVersion: 'default'|'DPM-13219' | 'DPM-13733';
    fundingTitle?: string;

    instruction?: string;
    bankHelperText?: string;

    debitCardInstructionText?: string;
    debitCardAgreementText?: string;
    debitHelperText?: string;

    routingError?: string;
    accountError?: string;
    reenterError?: string;
    fundingAttemptFailedErrorAch?: string;
    forcedFundingDebitOnly?: string;

    agreementText?: string;
    checkboxAgree?: string;
    sampleCheckPath?: string;
    nextPagePath?: string;
    ctaText?: string;
    aciPath?: string;

    variant?: TFundingModuleVariants;
    debitInFunding?: boolean;
    apiVersion?: 'old' | 'new';

    attemptsErrorRedirect?: string;
    attemptsExceededRedirect?: string;
    fundingErrorRedirect?: string;
    fundingApprovedRedirect?: string;
    fundingDeclinedRedirect?: string;

    selectAmtToFundText?: string;
    selectFundingText?: string;
    cardFundingHeader?: string;

    // Debit Card Funding--------------
    author?: boolean;
    instructionText?: string;
    nameLabel?: string;
    cardNumberLabel?: string;
    cardExpirationLabel?: string;
    cardSecurityLabel?: string;
    stateLabel?: string;
    zipLabel?: string;
    debitCardFailedPagePath?: string;
    verificationErrorCode?: string;
    processingErrorCode?: string;
    fundingErrorCode?: string;
    unprocessableEntityErrorCode?: string;
}

export const FundingEditConfig = {
    emptyLabel: 'Funding',
    isEmpty: (props: PropsWithChildren<IFundingProps>) => {
        return !props || !props.instruction;
    },
};

const enum FundingSource {
    BANK = 0,
    DEBIT = 1
}

export type TRadioType = {
    label: string,
    value: string,
    helperText: string,
}

// DPM-13219
export type CustomFundingValidationEvent = CustomEvent<{ errors: string[] }>;
export const enum FUNDING_ERROR_REGISTRY {
    FUNDING_METHOD_NO_ANCHOR = 'Please select a Funding Method',
    FUNDING_METHOD = 'Please select a <a href="#debit">Funding Method</a>',
    FUNDING_ACKNOWLEDGEMENT = 'Please agree to the <a href="#funding-information-field">Funding Acknowledgement</a>',
    ROUTING_NUMBER = 'A valid <a href="#routingNumber-field-container" id="cta-routingNumber">Routing Number</a> is required',
    ROUTING_NUMBER_OLD = 'Please provide a valid <a href="#routingNumber-field-container" id="cta-routingNumber">Routing Number</a>',
    ROUTING_NUMBER_EMPTY = '<a href="#routingNumber-field-container" id="cta-routingNumber">Routing Number</a> is required',
    ACCOUNT_NUMBER = 'A valid <a href="#accountNumber-field-container" id="cta-accountNumber">Account Number</a> is required',
    ACCOUNT_NUMBER_OLD = 'Please provide a valid <a href="#accountNumber-field-container" id="cta-accountNumber">Account Number</a>',
    ACCOUNT_NUMBER_EMPTY = '<a href="#accountNumber-field-container" id="cta-accountNumber">Account Number</a> is required',
    ACCOUNT_NUMBER_RE = 'Please <a href="#reAccountNumber-field-container" id="cta-reaccountNumber">re-enter the Account Number</a>',
    ACCOUNT_NUMBER_RE_REQUIRED = '<a href="#reAccountNumber-field-container" id="cta-reaccountNumber">Account Number</a> is required',
    ACCOUNT_NO_MATCH = 'Account Numbers <a href="#reAccountNumber-field-container">should match</a>',
}

export type TBankDetails = {
    valid: boolean,
    details?: {
        AccountType?: string,
        AccountNumber?: string,
        RoutingNumber?: string,
    }
}

export type TFundingModuleContext = {
    setBankDetails: React.Dispatch<TBankDetails>,
    bankDetails: TBankDetails,
}
const FundingModuleContext = React.createContext<TFundingModuleContext>({
    setBankDetails: () => { },
    bankDetails: { valid: false },
});
export const useFundingModuleContext = () => React.useContext(FundingModuleContext);

const FundingModule = (
    props: PropsWithChildren<IFundingProps>,
): JSX.Element => {
    const fundingAmountOptions = useRef<string[]>([
        200, 250,
        300, 350,
        400, 450,
        500,
    ].map((amt) => '$' + amt));
    const [amtToFund, setAmtToFund] = useState('$200');

    const [checkboxClickedAgreed, setCheckboxClickedAgreed] = useState(false);
    const [selectedFundingType, setSelectedFundingType] = useState<'debit'|'bank'|''>('');

    const [bankDetails, setBankDetails] = useState<TBankDetails>({ valid: false });
    const [inputRoutingNumber, setInputRoutingNumber] = useState('');
    const [inputAccountNumber, setInputAccountNumber] = useState('');
    const [inputVerifyAccountNumber, setInputVerifyAccountNumber] = useState('');
    const [variant, setVariant] = useState(props.variant);
    const routingNumberEl = useRef<HTMLInputElement>(null);
    const accountNumberEl = useRef<HTMLInputElement>(null);
    const reAccountNumberEl = useRef<HTMLInputElement>(null);
    const [ debitHasErrors , setDebitHasErrors] = useState(true);
    const isFundingSubmitting = useRef(false);

    const debitCardFailedPreviously = window.location.search.indexOf('fundVerify') > -1;

    const [isCtaEnabled, setIsCtaEnabled] = useState<boolean>(props.testingVersion === 'DPM-13219' ? true : false);
    const [isSpinning, setIsSpinning] = useState<boolean>(false);
    const [ pushupMessages, setPushupMessages ] = useState<Array<FUNDING_ERROR_REGISTRY>>([]);
    const [ isRoutingNumInError, setIsRoutingNumInError ] = useState(false);
    const [ isAccountNumInError, setIsAccountNumInError ] = useState(false);
    const [ isVerifyNumInError, setIsVerifyNumInError ] = useState(false);
    const [ doAccountNumbersMatch, setDoAccountNumbersMatch ] = useState(true);

    const submitValidationErrors = useRef<boolean>(false); // DPM-13219
    const bankForm = useRef<{validate: () => Array<FUNDING_ERROR_REGISTRY>}>(null); // DPM-13219

    const { showPushdown, closePushdown } = usePushdownContext();

    // transitioning height in css will break client height
    // use dynamic values to ensure client height is properly calculated
    const [height, setHeight] = useState<`${number}px`>('0px');
    const calculateHeight = useCallback(() => {
        if (selectedFundingType === 'bank') {
            const clientHeight = bankFormWrapper.current.clientHeight;
            const style = window.getComputedStyle(bankFormWrapper.current);
            const marginTop = parseInt(style.marginTop, 10) || 0;
            const marginBottom = parseInt(style.marginBottom, 10) || 0;

            setHeight(`${clientHeight + marginTop + marginBottom}px`);
        } else {
            setHeight('0px');
        }
    }, [selectedFundingType]);
    const bankFormWrapper = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!bankForm?.current || !bankFormWrapper.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            calculateHeight();
        });
        resizeObserver.observe(bankFormWrapper.current);

        return () => resizeObserver.disconnect();
    }, [calculateHeight])

    useEffect(() => {
        if (selectedFundingType === 'debit') {
            setIsRoutingNumInError(false);
            setIsAccountNumInError(false);
            setIsVerifyNumInError(false);
            setDoAccountNumbersMatch(true);
        }

        if (props.testingVersion === 'DPM-13219') {
            // DPM-13219: skip disabling CTA based on fields
            return;
        }

        if (!checkboxClickedAgreed) {
            setIsCtaEnabled(false);
            return;
        }
        if (selectedFundingType === 'bank') {
            setIsCtaEnabled(bankDetails.valid);
        } else if (selectedFundingType === 'debit') {
            if (props.debitInFunding) {
                setIsCtaEnabled(!debitHasErrors);
            } else {
                setIsCtaEnabled(true);
            }
        } else {
            setIsCtaEnabled(false);
        }
    }, [
        checkboxClickedAgreed,
        selectedFundingType,
        bankDetails,
        debitHasErrors,
    ]);

    const clearBankFields = () => {
        setInputAccountNumber('');
        setInputRoutingNumber('');
        setInputVerifyAccountNumber('');
    }

    // trigger validation via blur()
    const triggerBankFieldValidation = () => {
        accountNumberEl.current.dispatchEvent(new Event('blur'));
        reAccountNumberEl.current.dispatchEvent(new Event('blur'));
        routingNumberEl.current.dispatchEvent(new Event('blur'));
    }

    // Request actual field validations defined within the bankDetailInput component.
    const validateBankFields = () => {
        return bankForm?.current?.validate();
    }

    const validateFundingMethod = () => {
        return !selectedFundingType ? [FUNDING_ERROR_REGISTRY.FUNDING_METHOD] : [];
    }

    const validateFundingAcknowledgement = () => {
        return !checkboxClickedAgreed ? [FUNDING_ERROR_REGISTRY.FUNDING_ACKNOWLEDGEMENT] : [];
    }
    
    // DPM-13219
    const validateOnSubmit = () => {
        if (props.testingVersion !== 'DPM-13219') {
            return false;
        }

        const validationErrors: Array<FUNDING_ERROR_REGISTRY> = [];
        
        validationErrors.push(...validateFundingMethod());

        if (selectedFundingType === 'bank') {
            validationErrors.push(...validateBankFields());
            triggerBankFieldValidation();
        }

        validationErrors.push(...validateFundingAcknowledgement());

        if (Object.values(validationErrors).length > 0) {
            setPushupMessages(validationErrors);
            submitValidationErrors.current = true;
            showPushdown({
                header: 'Problem Processing Your Request.',
                description:
                    'The following fields and/or sections require your attention:',
                message: validationErrors,
                color: 'error',
            });
            return true;
        }
        
        closePushdown();
        submitValidationErrors.current = false;
        return false;
    }

    // applicationData can be either flowApplicationData or prequalFullAppData
    const createFundingACHRequestBody = async (applicationData) => {
            return {
                reservationNumber: sessionStorage.getItem('ReservationNumber') || applicationData?.reservationNumber || applicationData?.ReservationNumber || '',
                funding: {
                    bankAccount: {
                        accountNumber: bankDetails.details.AccountNumber,
                        accountType: bankDetails.details.AccountType,
                        routingNumber: bankDetails.details.RoutingNumber,
                    },
                    amountToFund: parseFloat(amtToFund.replace(/[^\d]/g, '')),
                },
                partnerData: {
                    partnerId: 'AEM',
                },
                requestMetadata: {
                    correlationId: UUIDv4(),
                    requestDateTime: Math.floor(new Date().getTime() / 1000),
                    requestedBy: 'AEM',
                    channel: 'WEB',
                    ipAddress: await getIpAddress(),
                },
            };
    };

    const redirect = useCallback((
        redirectTo: string
    ) => {
        window.location.assign(cleanInternalLinkUrl(redirectTo) + window.location.search);
    }, [props]);

    const callSubmitFundingApi = async (applicationData) => {
        const applicationID = SessionStorage.get('ApplicationId');
        if((getAcquisitionFlow() === AcquisitionFlow.PHA && applicationID !== null) || (getAcquisitionFlow() === AcquisitionFlow.PQ)){
        const formattedData = await createFundingACHRequestBody(applicationData);

        
        callAcquisitionBff('secured-card-funding', {
            replace: { 'applicationId': applicationID },
            body: formattedData,
        }).then(async (response: any) => {
            if (!response) {
                throw { response };
            }
            const responseData = response.data;
            SessionStorage.setObjectWithCurrentScope('fundingResponse', response.data);

            const parsedStatusDescription = responseData.applicationStatus.statusDescription.toUpperCase().replaceAll('_', ' ');
            if (responseData.applicationStatus.applicationStatus === 'Approved' && parsedStatusDescription === 'READY TO BOARD') {
                redirect(props.fundingApprovedRedirect);
            } else if (responseData.applicationStatus.applicationStatus === 'Declined') {
                redirect(props.fundingDeclinedRedirect);
            } else if (responseData.applicationStatus.applicationStatus === 'Ready To Fund') {
                switch (parsedStatusDescription) {
                    case 'SWITCH TO DEBIT':
                        // only ACH is enabled
                        if (props.variant === 'bankAccountEnabled') {
                            redirect(props.attemptsExceededRedirect);
                        }
                        
                        setVariant('debitCardEnabled');
                        setSelectedFundingType('debit');
                        setIsSpinning(false);
                        showPushdown(props?.forcedFundingDebitOnly);
                        break;
                    case 'EWS CHECK FAILED':
                        clearBankFields();
                        triggerBankFieldValidation();
                        showPushdown(props?.fundingAttemptFailedErrorAch);
                        setIsSpinning(false);
                        break;
                }
            } else if (responseData.applicationStatus.applicationStatus === 'Error' && parsedStatusDescription === 'REQUEST FAILED INTERNAL ERROR') {
                redirect(props.fundingErrorRedirect);
            }
        }).catch((err) => {
            console.error(err);
            redirect(props.fundingErrorRedirect);
        })
        .finally(() => {
            setIsCtaEnabled(true);
        });
        }
    };

    const handleCTA = useCallback(async () => {
        // DPM-13219
        if (validateOnSubmit()) {
            return;
        }

        isFundingSubmitting.current = true;
        setIsCtaEnabled(false);
        setIsSpinning(true);
        const { nextPage, fundingEnum } = {
            'bank': {
                nextPage: props.nextPagePath,
                fundingEnum: FundingSource.BANK,
            },
            'debit': {
                nextPage: props.aciPath,
                fundingEnum: FundingSource.DEBIT,
            },
        }[selectedFundingType];

        // from app status with the new api data format in flowApplicationData
        if ((getAcquisitionFlow() === AcquisitionFlow.PQ || getAcquisitionFlow() === AcquisitionFlow.PHA) && SessionStorage.verify('flowApplicationData')) {
            const flowApplicationData: IFlowApplicationData = SessionStorage.tryGetObject('flowApplicationData', {});
            delete flowApplicationData.funding; // reset funding data

            flowApplicationData.FundingPaymentSource = fundingEnum;
            flowApplicationData.funding = { amountToFund: parseFloat(amtToFund.replace(/[^\d]/g, '')) };

            // provide credit line to decision page for funding flow
            // only applicable for normal
            if (SessionStorage.verify('app-approved-data')) {
                const appApprovedData = await SessionStorage.getCipher('app-approved-data', true);
                appApprovedData.CreditLine = parseFloat(amtToFund.replace(/[^\d]/g, ''));
                SessionStorage.setObjectWithCurrentScope('app-approved-data', appApprovedData);
            }

            if (selectedFundingType === 'bank') {
                flowApplicationData.funding.bankAccount = {
                    accountNumber: bankDetails.details.AccountNumber,
                    accountType: bankDetails.details.AccountType,
                    routingNumber: bankDetails.details.RoutingNumber,
                };

                SessionStorage.setObjectWithCurrentScope('flowApplicationData', flowApplicationData);
                await callSubmitFundingApi(flowApplicationData);
            } else {
                const params = new URLSearchParams(window.location.search);
                params.append('fundVariant', props.variant);
                flowApplicationData.DebitFundingInformation = {
                    AmountToFund: parseFloat(amtToFund.replace(/[^\d]/g, '')),
                };
                SessionStorage.setObjectWithCurrentScope('flowApplicationData', flowApplicationData);
                if (!props.debitInFunding) {
                    
                    window.location.assign(cleanInternalLinkUrl(nextPage) + (params.toString().length ? '?' + params.toString() : ''));
                }
            }
        } else {  // normal flow
            const pqFullAppData = await SessionStorage.tryGetCipher('prequalFullAppData', {});

            delete pqFullAppData.DebitFundingInformation;
            delete pqFullAppData.FundingInformation;

            pqFullAppData.FundingPaymentSource = fundingEnum;

            if (selectedFundingType === 'bank') {
                pqFullAppData.FundingInformation = {
                    ...bankDetails.details,
                    AmountToFund: parseFloat(amtToFund.replace(/[^\d]/g, '')),
                    C1BAuthToFund: checkboxClickedAgreed,
                };
                await SessionStorage.setCipherWithCurrentScope('prequalFullAppData', pqFullAppData);
                await callSubmitFundingApi(pqFullAppData);
            } else {
                pqFullAppData.DebitFundingInformation = {
                    AmountToFund: parseFloat(amtToFund.replace(/[^\d]/g, '')),
                };
                await SessionStorage.setCipherWithCurrentScope('prequalFullAppData', pqFullAppData);
                const params = new URLSearchParams(window.location.search);
                params.append('fundVariant', props.variant);

                if (!props.debitInFunding) {
                    window.location.assign(cleanInternalLinkUrl(nextPage) + (params.toString().length ? '?' + params.toString() : ''));
                }
            }
        }
    }, [
        props,
        amtToFund,
        bankDetails,
        checkboxClickedAgreed,
        selectedFundingType,
    ]);

    const handleFundingAttempts = async () => {
        const applicationID = SessionStorage.get('ApplicationId');
        if(applicationID !== null){
            
            callAcquisitionBff<ISecuredCardFundingAttempt>('secured-card-funding-attempt', {
                replace: { 'applicationId': applicationID },
            }).then(async (response ) => {
                if (!response) {
                    throw { response };
                }
                const responseData = response.data;
                if (responseData.achAttemptsExceeded && (props.variant === 'bankAccountEnabled' || responseData.debitAttemptsExceeded)) {
                    redirect(props.attemptsExceededRedirect);
                } else if (responseData.achAttemptsExceeded && !responseData.debitAttemptsExceeded) {
                    // only ACH is enabled
                    if (props.variant === 'bankAccountEnabled' || debitCardFailedPreviously) {
                        redirect(props.attemptsExceededRedirect);
                    }

                    setVariant('debitCardEnabled');
                    setSelectedFundingType('debit');
                } else if (responseData.debitAttemptsExceeded && props.variant === 'debitCardEnabled') {
                    // only ACI is enabled
                    redirect(props.attemptsExceededRedirect);
                } else if (debitCardFailedPreviously) {
                    // Debit failed, revert to Bank

                    setVariant('bankAccountEnabled');
                    setSelectedFundingType('bank');
                } else {
                    setVariant(props.variant);
                }
                
            }).catch((err: Unknown) => {
                console.error(err);
                redirect(props.fundingErrorRedirect);
            });
        }
    };

    useEffect(() => {
        if (SessionStorage.verifyNotEmpty('flowApplicationData')) {
                const flowApplicationData: IFlowApplicationData = SessionStorage.tryGetObject('flowApplicationData', {});
                delete flowApplicationData.funding;
                SessionStorage.setObjectWithCurrentScope('flowApplicationData', flowApplicationData);
        }

        if (SessionStorage.verifyNotEmpty('prequalFullAppData')) {
            SessionStorage.tryGetCipher('prequalFullAppData', {}).then(async (prequalFullAppData) => {
                delete prequalFullAppData.DebitFundingInformation;
                delete prequalFullAppData.FundingInformation;
                await SessionStorage.setCipherWithCurrentScope('prequalFullAppData', prequalFullAppData);
            });
        }

        if ((getAcquisitionFlow() === AcquisitionFlow.PQ || getAcquisitionFlow() === AcquisitionFlow.PHA) && !isAuthorMode()) {
            if (props.variant === 'bankAccountEnabled') {
                setSelectedFundingType('bank');
            } else if (props.variant === 'debitCardEnabled') {
                setSelectedFundingType('debit');
            }

            handleFundingAttempts();
        } else if (debitCardFailedPreviously || props.variant === 'bankAccountEnabled') {
            setSelectedFundingType('bank');
        } else if (props.variant === 'debitCardEnabled') {
            setSelectedFundingType('debit');
        }
    }, [props]);

    const FundingAmountDropdown = useCallback(() => (
        <InputDropdown
            className={`FundingModule__amtToFundDropdown ${props.selectAmtToFundText ? 'FundingModule--bold': ''}`}
            id='amtToFund'
            name='amtToFund'
            label={props.selectAmtToFundText || 'Amount to Fund'}
            required={false}
            options={fundingAmountOptions.current}
            value={amtToFund}
            setValue={setAmtToFund}
        />
    ), [amtToFund, setAmtToFund]);
    
    const FundingInputRadio = useCallback((
        { label, value, helperText }: TRadioType , index: number
    ): JSX.Element => (
        <Col lg={12} sm={12} md={12}>
            <InputRadio
                key={index}
                label={<span className='FundingModule__Label'>{label}</span>}
                value={value}
                checked={selectedFundingType === value}
                setValue={setSelectedFundingType as (e: string) => void}
                name='fundingType'
                id={value}
                error={props.testingVersion === 'DPM-13219' && submitValidationErrors.current && selectedFundingType === ''}
            />
            {helperText && (
                <p className='FundingModule__helperText'>
                    {transformHTMLParserLinks(helperText)}
                </p>
            )}
        </Col>
    ), [selectedFundingType, setSelectedFundingType]);

    useEffect(()=> {
        if (!props.debitInFunding) {
            return;
        }
       closePushdown();
    }, [selectedFundingType])

    useEffect(() => {
        const errorMessages: Array<FUNDING_ERROR_REGISTRY> = [];

        // DPM-13219
        if (props.testingVersion === 'DPM-13219' && submitValidationErrors.current && !selectedFundingType) {
            errorMessages.push(FUNDING_ERROR_REGISTRY.FUNDING_METHOD);
        }
        if (isRoutingNumInError) {
            if (!isNewErrorMessagingFlagActive) {
                errorMessages.push(FUNDING_ERROR_REGISTRY.ROUTING_NUMBER_OLD);
            } else if (routingNumberEl?.current?.value === '') {
                errorMessages.push(FUNDING_ERROR_REGISTRY.ROUTING_NUMBER_EMPTY);
            } else {
                errorMessages.push(FUNDING_ERROR_REGISTRY.ROUTING_NUMBER);
            }
        }
        if (isAccountNumInError) {
            if (!isNewErrorMessagingFlagActive) {
                errorMessages.push(FUNDING_ERROR_REGISTRY.ACCOUNT_NUMBER_OLD);
            } else {
                errorMessages.push(FUNDING_ERROR_REGISTRY.ACCOUNT_NUMBER_EMPTY);
            }
        }
        if (isVerifyNumInError) {
            errorMessages.push(isNewErrorMessagingFlagActive ? FUNDING_ERROR_REGISTRY.ACCOUNT_NUMBER_RE_REQUIRED : FUNDING_ERROR_REGISTRY.ACCOUNT_NUMBER_RE);
        } else if (!doAccountNumbersMatch) {
            errorMessages.push(FUNDING_ERROR_REGISTRY.ACCOUNT_NO_MATCH);
        }
        // DPM-13219
        if (props.testingVersion === 'DPM-13219' && submitValidationErrors.current && !checkboxClickedAgreed) {
            errorMessages.push(FUNDING_ERROR_REGISTRY.FUNDING_ACKNOWLEDGEMENT);
        }

        setPushupMessages(errorMessages);
    }, [selectedFundingType, isRoutingNumInError, isAccountNumInError, isVerifyNumInError, doAccountNumbersMatch, checkboxClickedAgreed]);

    return (
        <FundingModuleContext.Provider value={{ bankDetails, setBankDetails }}>
            {isSpinning ? <AcquisitionsLoader processingSteps={[]} /> : <></>}
            <div className={isSpinning ? 'hidden' : 'block'}>
            <form className='FundingModule__form block font-semibold pb-4'>
                <Section className='Funding !py-0 h-auto'>
                    <Container className='!py-0 !px-3'>
                        <Row>
                            <div className='w-full mt-4 FundingModule__title'>
                                <p>
                                    {transformHTMLParserLinks(wrapTrademarkSuperscript(props.fundingTitle || ''))}
                                </p>
                            </div>
                            <div className='w-100 mt-4'>
                                <p>
                                    {transformHTMLParserLinks(
                                        (variant === 'debitCardEnabled'
                                            ? props.debitCardInstructionText
                                            : props.instruction) || '',
                                    )}
                                </p>
                            </div>
                        </Row>
                        <Row>
                            <Col lg={12} sm={12} md={12}>
                                <FundingAmountDropdown />
                            </Col>
                        </Row>
                        {props.debitInFunding ?
                        <>
                            <Row justify='start'>
                                {
                                    variant === 'defaultEnabled' &&
                                    !debitCardFailedPreviously &&
                                    <>
                                        {props.selectFundingText && <Col className={'FundingModule__selectFundingText'} lg={12} sm={12} md={12}>
                                        {props.selectFundingText}
                                        </Col>}
                                        <FundingInputRadio {...{
                                            label: 'Debit Card',
                                            value: 'debit',
                                            helperText: props.debitHelperText,
                                        }} />
                                    </>
                                }
                            </Row>
                            <div className={'FundingModule__hiderSlider' + (selectedFundingType === 'debit' || debitCardFailedPreviously ? ' hiderSlider--shown' : '')}>
                                {(selectedFundingType === 'debit') && (
                                    <div className={variant === 'defaultEnabled' ? 'FundingModule__bankAndDebitSingleForm' : ''}>
                                        <DebitCardFunding 
                                            {...(props as any)} 
                                            isFundingSubmitting={isFundingSubmitting}
                                            singlePageFunding={true}
                                            setIsSpinning={setIsSpinning}
                                            setDebitHasErrors={setDebitHasErrors}/>
                                    </div>
                                )}
                            </div>
                            <Row justify='start'>
                                {
                                    variant === 'defaultEnabled' &&
                                    !debitCardFailedPreviously &&
                                    <>
                                        <FundingInputRadio {...{
                                            label: 'Bank Account',
                                            value: 'bank',
                                            helperText: props.bankHelperText,
                                        }} />
                                    </>
                                }
                            </Row>
                            <div className={'FundingModule__hiderSlider' + ((selectedFundingType === 'bank' || debitCardFailedPreviously) ? ' hiderSlider--shown' : '')}
                                style={{ height: height }}>
                                {(selectedFundingType === 'bank') && (
                                    <div ref={bankFormWrapper} className={variant === 'defaultEnabled' ? 'FundingModule__bankAndDebitSingleForm' : ''}>
                                        <BankDetailInput {...(props as any)}
                                            ref={bankForm}
                                            isRoutingNumInError={isRoutingNumInError}
                                            setIsRoutingNumInError={setIsRoutingNumInError}
                                            isAccountNumInError={isAccountNumInError}
                                            setIsAccountNumInError={setIsAccountNumInError}
                                            isVerifyNumInError={isVerifyNumInError}
                                            setIsVerifyNumInError={setIsVerifyNumInError}
                                            doAccountNumbersMatch={doAccountNumbersMatch}
                                            setDoAccountNumbersMatch={setDoAccountNumbersMatch}
                                            inputAccountNumber={inputAccountNumber}
                                            inputRoutingNumber={inputRoutingNumber}
                                            inputVerifyAccountNumber={inputVerifyAccountNumber}
                                            setInputAccountNumber={setInputAccountNumber}
                                            setInputRoutingNumber={setInputRoutingNumber}
                                            setInputVerifyAccountNumber={setInputVerifyAccountNumber}
                                            routingNumberEl={routingNumberEl}
                                            reAccountNumberEl={reAccountNumberEl}
                                            accountNumberEl={accountNumberEl}
                                            singlePageFunding={true}
                                        />
                                    </div>
                                )}
                            </div>
                        </> :
                        <>
                            <Row justify='start'>
                                {
                                    variant === 'defaultEnabled' &&
                                    !debitCardFailedPreviously && props.testingVersion !== 'DPM-13733' &&
                                    <>
                                        {props.selectFundingText && <Col className={'FundingModule__selectFundingText'} lg={12} sm={12} md={12}>
                                        {props.selectFundingText}
                                        </Col>}
                                        <FundingInputRadio {...{
                                            label: 'Debit Card',
                                            value: 'debit',
                                            helperText: props.debitHelperText,
                                        }} />
                                        <FundingInputRadio {...{
                                            label: 'Bank Account',
                                            value: 'bank',
                                            helperText: props.bankHelperText,
                                        }} />
                                        {/* DPM-13219 */}
                                        {props.testingVersion === 'DPM-13219' && submitValidationErrors.current && selectedFundingType === '' && (
                                            <p className='InputBase__description--errorWithExclamation pl-2'>
                                                {transformHTMLParserLinks(FUNDING_ERROR_REGISTRY.FUNDING_METHOD_NO_ANCHOR)}
                                            </p>
                                        )}
                                    </>
                                }
                                {
                                    props.testingVersion === 'DPM-13733' && variant === 'defaultEnabled' && !debitCardFailedPreviously && 
                                    <FundingModuleSelectableCards 
                                        title={props.cardFundingHeader}
                                        selectedFundingType={selectedFundingType}
                                        setSelectedFundingType={setSelectedFundingType}
                                        debitHelperText={props.debitHelperText}
                                        bankHelperText={props.bankHelperText}/>
                                }
                            </Row>
                            <div className={'FundingModule__hiderSlider' + ((selectedFundingType === 'bank' || debitCardFailedPreviously) ? ' hiderSlider--shown' : '')}
                                style={{ height: height }}>
                                {(selectedFundingType === 'bank') && (
                                    <div ref={bankFormWrapper} className={variant === 'defaultEnabled' ? 'FundingModule__bankAndDebitSingleForm' : ''}>
                                        <BankDetailInput {...props}
                                            ref={bankForm}
                                            isRoutingNumInError={isRoutingNumInError}
                                            setIsRoutingNumInError={setIsRoutingNumInError}
                                            isAccountNumInError={isAccountNumInError}
                                            setIsAccountNumInError={setIsAccountNumInError}
                                            isVerifyNumInError={isVerifyNumInError}
                                            setIsVerifyNumInError={setIsVerifyNumInError}
                                            doAccountNumbersMatch={doAccountNumbersMatch}
                                            setDoAccountNumbersMatch={setDoAccountNumbersMatch}
                                            inputAccountNumber={inputAccountNumber}
                                            inputRoutingNumber={inputRoutingNumber}
                                            inputVerifyAccountNumber={inputVerifyAccountNumber}
                                            setInputAccountNumber={setInputAccountNumber}
                                            setInputRoutingNumber={setInputRoutingNumber}
                                            setInputVerifyAccountNumber={setInputVerifyAccountNumber}
                                            routingNumberEl={routingNumberEl}
                                            reAccountNumberEl={reAccountNumberEl}
                                            accountNumberEl={accountNumberEl}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                        }
                        <Row>
                            <Col lg={12} sm={12} md={12}>
                                <div className='w-100 mt-4'>
                                    <p>
                                        {transformHTMLParserLinks(wrapTrademarkSuperscript(
                                            (variant === 'debitCardEnabled'
                                                ? props.debitCardAgreementText
                                                : props.agreementText) || '',
                                        ))}
                                    </p>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            {/* DPM-13219: avoid browser default focus behavior when anchor is attached to checkbox input itself */}
                            <Col id="funding-information-field">
                                <InputCheckbox
                                    name='funding-information'
                                    label={
                                        <span className='FundingModule__Label'>
                                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.checkboxAgree || ''))}
                                        </span>}
                                    setValue={setCheckboxClickedAgreed}
                                    value={checkboxClickedAgreed}
                                    id='funding-information'
                                    ariaLabel={'Yes, I Agree'}
                                    error={props.testingVersion === 'DPM-13219' && submitValidationErrors.current && !checkboxClickedAgreed} // DPM-13219
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <InlinePushup messages={pushupMessages} />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className={`FundingModule__ctaButton ${pushupMessages.length ? 'FundingModule__ctaButton--showingMessages' : ''}`}>
                                    {props.debitInFunding && selectedFundingType === 'debit' ? 
                                        <input className='Button DebitCardFunding__submitButtonOverride' 
                                            type='button' 
                                            data-aci-speedpay='card-submit-button' 
                                            disabled={!isCtaEnabled}
                                            onClick={async (event) => {
                                                    event.currentTarget.disabled = true;
                                                    await handleCTA();
                                                }}
                                            value={wrapTrademarkSuperscript(props.ctaText || '')}>
                                        </input> :
                                        <>
                                            <Button
                                                onClick={async () => {
                                                    await handleCTA();
                                                }}
                                                size='medium'
                                                variant='solid'
                                                disabled={!isCtaEnabled}>
                                                {wrapTrademarkSuperscript(props.ctaText || '')}
                                            </Button>
                                        </>
                                    }
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </Section>
            </form>
            </div>
        </FundingModuleContext.Provider>
    );
};

export default FundingModule;
